import React, { Suspense, useEffect, useMemo, useRef, useState } from "react";
import { Box, ButtonBase, useTheme } from "@mui/material";
import { ChevronLeftRounded, ChevronRightRounded } from "@mui/icons-material";
import { useLocation, useNavigationType } from "react-router-dom";
import DashboardOverviewTab from "./DashboardOverviewTab";
import RadarSkeleton from "./Radar/RadarSkeleton";
import SectorsSkeleton from "./Radar/SectorsSkeleton";
import {
  consumeDashboardScroll,
  recallDashboardTab,
  rememberDashboardPlace,
} from "./dashboardMemory";
import { hapticSelection } from "../../lib/haptics";
import { useTopBarActions, useTopBarLeading } from "../../contexts/TopBarSlotContext";
import { panelTitleSx } from "./overviewPanel";
import { isNative } from "../../lib/platform";
import { lazyWithReload } from "../../utils/lazyWithReload";

// Sectors and Radar are lazy so Overview users never download their bundle;
// lazyWithReload self-heals post-deploy chunk skew like every other lazy
// route.
const SectorsTab = lazyWithReload(() => import("./Radar/SectorsTab"));
const RadarTab = lazyWithReload(() => import("./Radar/RadarTab"));

// The dashboard is a set of pages navigated by a floating pager pinned
// bottom-center: a segmented pill naming every page, the current one lit in
// insight green. Labels rather than dots — anonymous dots hide what the
// pages ARE, and a new user never taps what they can't name. Adding a page
// is one entry. Sectors is the theme explorer; Radar is the heatmap pair.
const PAGES = [
  { key: "overview", label: "Overview" },
  { key: "sectors", label: "Sectors" },
  { key: "radar", label: "Radar" },
] as const;

type DashboardTab = (typeof PAGES)[number]["key"];

// How long the outgoing page takes to slide away before the swap.
const EXIT_MS = 150;

// On native the floating pager gives way to the top bar: the pages either
// side of the current one sit left and right of the wordmark as quiet
// chevron-and-label links in the section headers' own idiom, so switching
// pages reads as part of the app's chrome rather than a control floating
// over the content — and nothing competes with the bottom tab bar.
interface HeaderPageLinkProps {
  label: string;
  direction: "previous" | "next";
  onSelect: () => void;
}

const HeaderPageLink: React.FC<HeaderPageLinkProps> = ({ label, direction, onSelect }) => {
  const theme = useTheme();
  const isPrevious = direction === "previous";
  return (
    <ButtonBase
      disableRipple
      onClick={onSelect}
      aria-label={`${isPrevious ? "Previous" : "Next"} page: ${label}`}
      sx={{
        display: "flex",
        alignItems: "center",
        gap: 0.25,
        height: 40,
        px: 0.5,
        ml: isPrevious ? -0.75 : 0,
        mr: isPrevious ? 0 : -0.75,
        borderRadius: 1,
        color: theme.palette.text.secondary,
        WebkitTapHighlightColor: "transparent",
        transition: "color 0.15s ease, transform 0.1s ease",
        "&:active": { color: "#22c55e", transform: "scale(0.96)" },
        "&:focus-visible": { outline: "2px solid #22c55e", outlineOffset: "2px" },
      }}
    >
      {isPrevious && <ChevronLeftRounded sx={{ fontSize: 20, ml: -0.25 }} />}
      <Box component="span" sx={{ ...panelTitleSx, lineHeight: 1 }}>
        {label}
      </Box>
      {!isPrevious && <ChevronRightRounded sx={{ fontSize: 20, mr: -0.25 }} />}
    </ButtonBase>
  );
};

const isDashboardTab = (value: string | undefined): value is DashboardTab =>
  PAGES.some((page) => page.key === value);

// Page state is deliberately internal — no URL params, clean address bar.
// How the user ARRIVED decides where they land: back/forward (a history
// POP) restores the remembered page and scroll, because going back means
// "return me to where I was" — but an explicit navigation (the sidebar's
// Dashboard button, the native tab bar) is an absolute action and always
// lands on the Overview root, sub-state overridden.
const DashboardV2: React.FC = () => {
  const theme = useTheme();
  const isDark = theme.palette.mode === "dark";
  const location = useLocation();
  const navigationType = useNavigationType();

  const arrivedByHistory = navigationType === "POP";

  const [tab, setTab] = useState<DashboardTab>(() => {
    if (!arrivedByHistory) return "overview";
    const remembered = recallDashboardTab();
    return isDashboardTab(remembered) ? remembered : "overview";
  });
  const [enterFrom, setEnterFrom] = useState<"left" | "right">("right");

  // Where the user was is captured at unmount — the moment they leave for
  // another route — while the window still holds the dashboard's scroll.
  const tabRef = useRef(tab);
  tabRef.current = tab;
  useEffect(() => {
    return () => rememberDashboardPlace(tabRef.current, window.scrollY);
  }, []);

  // Returning via history: wait for the page's content to grow tall enough
  // (skeletons give way to real sections over a few frames), then jump back
  // to the remembered offset. One-shot; gives up quietly after ~2s. An
  // explicit navigation discards the remembered offset instead.
  useEffect(() => {
    const savedScroll = consumeDashboardScroll();
    if (!savedScroll || !arrivedByHistory) {
      // Explicit navigation is absolute: land at the top regardless of the
      // scroll the previous route left behind — the router restores nothing
      // by itself, so without this a scrolled stock page bleeds its offset
      // into a fresh dashboard mount.
      if (!arrivedByHistory) window.scrollTo(0, 0);
      return;
    }
    let raf = 0;
    let frames = 0;
    const attempt = () => {
      const maxScroll = document.documentElement.scrollHeight - window.innerHeight;
      if (maxScroll >= savedScroll || frames > 120) {
        window.scrollTo(0, Math.min(savedScroll, Math.max(maxScroll, 0)));
        return;
      }
      frames += 1;
      raf = requestAnimationFrame(attempt);
    };
    raf = requestAnimationFrame(attempt);
    return () => cancelAnimationFrame(raf);
    // Mount-only by design: the arrival type is read once here — re-running
    // on a later in-place navigation would replay the restore.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Clicking the sidebar's Dashboard button while ALREADY on the dashboard
  // pushes a fresh history entry without remounting — treat it the same as
  // any explicit navigation: back to the Overview root, scrolled to the top.
  const lastLocationKeyRef = useRef(location.key);
  useEffect(() => {
    if (location.key === lastLocationKeyRef.current) return;
    lastLocationKeyRef.current = location.key;
    if (navigationType === "POP") return;
    if (tabRef.current !== "overview") {
      setEnterFrom("left");
      setTab("overview");
    }
    window.scrollTo(0, 0);
  }, [location.key, navigationType]);

  const pageIndex = PAGES.findIndex((page) => page.key === tab);

  // Page changes are a two-phase choreography: the outgoing page slides
  // away in the travel direction first, THEN the incoming page plays its
  // entrance — one continuous journey instead of a hard cut to a fresh
  // mount. `pendingTab` holds the destination while the exit plays; the
  // pager lights the destination immediately so the tap feels instant.
  const [pendingTab, setPendingTab] = useState<DashboardTab | null>(null);

  useEffect(() => {
    if (!pendingTab) return;
    const timer = window.setTimeout(() => {
      setTab(pendingTab);
      setPendingTab(null);
      window.scrollTo(0, 0);
    }, EXIT_MS);
    return () => window.clearTimeout(timer);
  }, [pendingTab]);

  const goTo = (nextIndex: number) => {
    if (nextIndex === pageIndex || pendingTab) return;
    hapticSelection();
    const next = PAGES[nextIndex].key;
    setEnterFrom(nextIndex > pageIndex ? "right" : "left");
    // Reduced motion: no choreography, just the swap.
    if (window.matchMedia?.("(prefers-reduced-motion: reduce)").matches) {
      setTab(next);
      window.scrollTo(0, 0);
      return;
    }
    setPendingTab(next);
  };

  const activeTabKey = pendingTab ?? tab;

  // The header links follow the DESTINATION while a change is pending, so a
  // tap reads instantly. goTo is a fresh closure every render; the memoised
  // nodes reach it through a ref so the top bar only re-renders on a real
  // page change.
  const goToRef = useRef(goTo);
  goToRef.current = goTo;
  const activeIndex = PAGES.findIndex((page) => page.key === activeTabKey);
  const previousPage = PAGES[activeIndex - 1];
  const nextPage = PAGES[activeIndex + 1];
  const leadingLink = useMemo(
    () =>
      previousPage ? (
        <HeaderPageLink
          label={previousPage.label}
          direction="previous"
          onSelect={() => goToRef.current(activeIndex - 1)}
        />
      ) : null,
    [previousPage, activeIndex],
  );
  const trailingLink = useMemo(
    () =>
      nextPage ? (
        <HeaderPageLink
          label={nextPage.label}
          direction="next"
          onSelect={() => goToRef.current(activeIndex + 1)}
        />
      ) : null,
    [nextPage, activeIndex],
  );
  useTopBarLeading(leadingLink, isNative());
  useTopBarActions(trailingLink, isNative());

  return (
    <Box
      sx={{
        minHeight: "100vh",
        backgroundColor: isDark ? "#1a1a1a" : "#ffffff",
        // No overflow on this root: the WINDOW is the scroller, and any
        // overflow other than visible here would become the scrollport for
        // the pages' position: sticky (the Sectors chart) — it never sticks.
        // Hide scrollbar
        scrollbarWidth: "none", // Firefox
        msOverflowStyle: "none", // IE/Edge
        "&::-webkit-scrollbar": {
          display: "none", // Chrome/Safari
        },
      }}
    >
      {/* Main Content Container */}
      <Box
        sx={{
          maxWidth: 1600,
          mx: "auto",
          px: { xs: 2, md: 3 },
          pt: { xs: 2, md: 2.5 },
          // Clear space so the floating pager never covers the page's tail;
          // native has no pager (the top bar carries the page links).
          pb: { xs: isNative() ? 4 : 10, md: 11 },
        }}
      >
        {/* Keyed by page so each change re-enters from the travel
            direction; while a change is pending the CURRENT page exits the
            opposite way ("forwards" holds it faded until the swap).
            Transform/opacity only; disabled under reduced motion. */}
        <Box
          key={tab}
          sx={{
            animation: pendingTab
              ? `dashPageOut ${EXIT_MS}ms cubic-bezier(0.4, 0, 1, 1) forwards`
              : "dashPageIn 0.28s cubic-bezier(0.22, 1, 0.36, 1)",
            "@keyframes dashPageIn": {
              from: {
                opacity: 0,
                transform: enterFrom === "right" ? "translateX(24px)" : "translateX(-24px)",
              },
              to: { opacity: 1, transform: "none" },
            },
            "@keyframes dashPageOut": {
              from: { opacity: 1, transform: "none" },
              to: {
                opacity: 0,
                transform: enterFrom === "right" ? "translateX(-20px)" : "translateX(20px)",
              },
            },
            "@media (prefers-reduced-motion: reduce)": { animation: "none" },
          }}
        >
          {tab === "overview" && <DashboardOverviewTab />}
          {tab === "sectors" && (
            <Suspense fallback={<SectorsSkeleton />}>
              <SectorsTab />
            </Suspense>
          )}
          {tab === "radar" && (
            <Suspense fallback={<RadarSkeleton />}>
              <RadarTab />
            </Suspense>
          )}
        </Box>
      </Box>

      {/* Floating pager — web only, fixed bottom-center. Every page is
          named and directly tappable; the current one reads green on a
          green wash, the house selected-state everywhere else. On native the
          top bar's chevron links replace it (see HeaderPageLink). */}
      {!isNative() && (
      <Box
        sx={{
          position: "fixed",
          left: "50%",
          transform: "translateX(-50%)",
          bottom: `calc(${isNative() ? 72 : 20}px + env(safe-area-inset-bottom, 0px))`,
          zIndex: 20,
          display: "flex",
          alignItems: "center",
          gap: 0.5,
          p: 0.5,
          borderRadius: 999,
          backgroundColor: isDark ? "#212121" : "#f5f5f5",
          border: isDark ? "1px solid rgba(255,255,255,0.1)" : "1px solid rgba(0,0,0,0.1)",
        }}
      >
        {PAGES.map((page, index) => {
          const selected = page.key === activeTabKey;
          return (
            <ButtonBase
              key={page.key}
              disableRipple
              onClick={() => goTo(index)}
              aria-current={selected ? "page" : undefined}
              sx={{
                px: 1.5,
                py: 0.625,
                borderRadius: 999,
                fontFamily: "inherit",
                fontSize: "0.75rem",
                fontWeight: 600,
                lineHeight: 1,
                whiteSpace: "nowrap",
                color: selected ? "#22c55e" : theme.palette.text.secondary,
                backgroundColor: selected ? "rgba(34, 197, 94, 0.12)" : "transparent",
                transition: "color 0.2s ease, background-color 0.2s ease",
                "&:hover": {
                  color: selected ? "#22c55e" : theme.palette.text.primary,
                },
                // ButtonBase zeroes the UA outline, so the pager must put a
                // keyboard focus ring back itself.
                "&:focus-visible": { outline: "2px solid #22c55e", outlineOffset: "2px" },
              }}
            >
              {page.label}
            </ButtonBase>
          );
        })}
      </Box>
      )}
    </Box>
  );
};

export default DashboardV2;
